import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, today, formatApiError, hasWrite, getRoles, getTabCompany } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { SignaturePad } from "../components/SignaturePad";
import { SalaryRegister } from "../components/SalaryRegister";
import { SalaryTrend } from "../components/SalaryTrend";
import { EmpCodeImport } from "../components/EmpCodeImport";
import { askReason } from "../components/ApprovalLock";
import { UserPlus, Users, Trash2, Pencil, Wallet, FileDown, Download, Wrench, Lock, Undo2, Printer, Upload } from "lucide-react";
import { toast } from "sonner";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";

const statusColors = {
  draft: "bg-muted text-muted-foreground",
  pending: "bg-accent/15 text-accent",
  final: "bg-primary/15 text-primary",
  paid: "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]",
};

const curMonth = () => new Date().toISOString().slice(0, 7);

const LEAVE_TYPES = [
  { v: "none", label: "Without Leaves" },
  { v: "two", label: "With 2 Days Leave" },
  { v: "four", label: "With 4 Leaves" },
];
const LEAVE_BONUS = { none: 0, two: 2, four: 4 };
const LEAVE_THRESHOLD = 24;
const leaveLabel = (v) => (LEAVE_TYPES.find((t) => t.v === v) || LEAVE_TYPES[0]).label;
const bonusFor = (lt, present) => (present >= LEAVE_THRESHOLD ? (LEAVE_BONUS[lt] || 0) : 0);

export default function Salary() {
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const [month, setMonth] = useState(curMonth());
  const [rows, setRows] = useState([]);
  const [printingAll, setPrintingAll] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [importRes, setImportRes] = useState(null);
  const [importing, setImporting] = useState(false);
  const attRef = useRef(null);

  const [selected, setSelected] = useState(new Set());
  const [present, setPresent] = useState({});
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", emp_code: "", monthly_salary: "", designation: "", phone: "", leave_type: "none" });
  const [pay, setPay] = useState(null);
  const [payMode, setPayMode] = useState("cash");
  const [payDate, setPayDate] = useState(today());
  const [adv, setAdv] = useState(null);
  const [advForm, setAdvForm] = useState({ amount: "", mode: "cash", note: "", date: today() });
  const payCashierRef = useRef(null);
  const payReceiverRef = useRef(null);
  const advCashierRef = useRef(null);
  const advReceiverRef = useRef(null);
  const [advLog, setAdvLog] = useState(null);
  const [advLogRows, setAdvLogRows] = useState([]);
  const [assetCounts, setAssetCounts] = useState({});
  const [fin, setFin] = useState(null);
  const [finAll, setFinAll] = useState(false);
  const isAdmin = getRoles(user).includes("admin");

  const load = useCallback(async () => {
    const { data } = await api.get("/salary", { params: { month } });
    setRows(data);
    const pr = {};
    data.forEach((r) => { pr[r.employee_id] = r.present_days; });
    setPresent(pr);
    setSelected(new Set());
  }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/assets/allotted-counts").then((r) => setAssetCounts(r.data || {})).catch(() => setAssetCounts({})); }, []);

  const resetForm = () => { setForm({ name: "", emp_code: "", monthly_salary: "", designation: "", phone: "", leave_type: "none" }); setEdit(null); };

  const saveEmp = async () => {
    try {
      if (!form.name) return toast.error("Employee name required");
      const payload = {
        name: form.name, emp_code: form.emp_code, monthly_salary: parseFloat(form.monthly_salary) || 0,
        designation: form.designation, phone: form.phone, leave_type: form.leave_type || "none",
      };
      if (edit) {
        const edit_reason = askReason(user, "editing", `employee ${form.name}`);
        if (edit_reason === null) return;
        const res = await api.put(`/employees/${edit}`, { ...payload, edit_reason });
        if (res.data?.status === "pending") { toast.info(res.data.message); setOpen(false); resetForm(); return; }
      } else await api.post("/employees", payload);
      toast.success(edit ? "Employee updated" : "Employee added");
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (r) => {
    setEdit(r.employee_id);
    setForm({ name: r.name, emp_code: r.emp_code || "", monthly_salary: String(r.monthly_salary || ""), designation: r.designation || "", phone: r.phone || "", leave_type: r.leave_type || "none" });
    setOpen(true);
  };

  const delEmp = async (r) => {
    if (!window.confirm(`Delete ${r.name}? Salary history for this employee will be removed.`)) return;
    try { await api.delete(`/employees/${r.employee_id}`); toast.success("Employee deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveLeave = async (r) => {
    try {
      await api.post("/salary", { employee_id: r.employee_id, month, present_days: parseFloat(present[r.employee_id]) || 0 });
      toast.success(`Attendance saved for ${r.name}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doPay = async () => {
    try {
      const cs = payCashierRef.current?.getData() || "";
      const rs = payReceiverRef.current?.getData() || "";
      if (!cs || !rs) return toast.error("Both cashier & receiver signatures are required");
      let rid = pay.record_id;
      if (!rid || pay.status === "draft") {
        const { data } = await api.post("/salary", { employee_id: pay.employee_id, month, present_days: parseFloat(present[pay.employee_id]) || 0 });
        rid = data.id;
      }
      await api.post(`/salary/${rid}/pay`, { mode: payMode, date: payDate, cashier_signature: cs, receiver_signature: rs });
      toast.success("Salary paid & posted to Cash Book expenses");
      setPay(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doFinal = async () => {
    try {
      const { data } = await api.post("/salary/finalize", { employee_id: fin.employee_id, month, present_days: parseFloat(present[fin.employee_id] ?? fin.present_days) || 0 });
      toast.success(`Month finalised · payable ${inr(data.payable)} posted to ledger`);
      setFin(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const finRows = rows.filter((r) => r.status !== "final" && r.status !== "paid");
  const finPay = (r) => Math.max(netLive(r) - (r.advance_total || 0), 0);
  const doFinalAll = async () => {
    try {
      const items = finRows.map((r) => ({ employee_id: r.employee_id, month, present_days: parseFloat(present[r.employee_id] ?? r.present_days) || 0 }));
      const { data } = await api.post("/salary/finalize-all", { month, items });
      toast.success(`${data.finalized} employee(s) finalised · payable ${inr(data.total_payable)}${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setFinAll(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const undoFinal = async (r) => {
    if (!window.confirm(`Undo month-end final for ${r.name} (${month})?`)) return;
    try { await api.post(`/salary/${r.record_id}/unfinalize`); toast.success("Final undone"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveAdvance = async () => {
    try {
      if (!advForm.amount) return toast.error("Amount required");
      const cs = advCashierRef.current?.getData() || "";
      const rs = advReceiverRef.current?.getData() || "";
      if (!cs || !rs) return toast.error("Both cashier & receiver signatures are required");
      await api.post("/salary/advance", { employee_id: adv.employee_id, month, amount: parseFloat(advForm.amount), mode: advForm.mode, date: advForm.date || today(), note: advForm.note, cashier_signature: cs, receiver_signature: rs });
      toast.success("Advance paid & posted to Cash Book expenses");
      setAdv(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openAdvLog = async (r) => {
    setAdvLog(r);
    try { setAdvLogRows((await api.get("/salary/advances", { params: { employee_id: r.employee_id } })).data); }
    catch { setAdvLogRows([]); }
  };

  const paidDaysLive = (r) => {
    const raw = present[r.employee_id];
    const p = raw === undefined || raw === "" ? (r.present_days ?? 30) : parseFloat(raw) || 0;
    return p + bonusFor(r.leave_type, p);
  };
  const netLive = (r) => {
    if (r.status === "paid") return r.net;
    const perDay = (r.monthly_salary || 0) / 30;
    return Math.max(paidDaysLive(r) * perDay, 0);
  };

  const importAttendance = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/salary/attendance-import", fd, { params: { month } });
      setImportRes(data);
      toast.success(`Attendance filled for ${data.updated.length} employee(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
      if (attRef.current) attRef.current.value = "";
    }
  };
  const printAllSlips = async () => {
    setPrintingAll(true);
    try {
      const res = await api.get("/salary/all-slips", { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (w) w.addEventListener("load", () => w.print());
      else toast.error("Allow pop-ups to print the slips");
    } catch {
      toast.error("Could not create salary slips");
    } finally {
      setPrintingAll(false);
    }
  };
  const downloadSlip = async (r) => {
    try {
      const res = await api.get(`/employees/${r.employee_id}/month-slip`, { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (w) w.addEventListener("load", () => w.print());
      else toast.error("Allow pop-ups to print the slip");
    } catch {
      toast.error("Could not download payslip");
    }
  };
  const totalNet = rows.reduce((s, r) => s + netLive(r), 0);
  const totalPaid = rows.filter((r) => r.status === "paid").reduce((s, r) => s + r.net, 0);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.employee_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.employee_id)));

  const exportSelected = () => {
    const picked = rows.filter((r) => selected.has(r.employee_id));
    if (picked.length === 0) return toast.error("Select at least one employee");
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["name", "designation", "leave_type", "monthly_salary", "present_days", "paid_days", "net", "advance", "payable", "status", "month"];
    const lines = [header.join(",")].concat(picked.map((r) => [r.name, r.designation, leaveLabel(r.leave_type), r.monthly_salary, present[r.employee_id] ?? r.present_days, paidDaysLive(r), netLive(r), r.advance_total, r.payable, r.status, month].map(esc).join(",")));
    const blob = new Blob([lines.join("\n") + "\n"], { type: "text/csv" });
    const el = document.createElement("a");
    el.href = URL.createObjectURL(blob);
    el.download = `salary_${month}.csv`;
    el.click();
    URL.revokeObjectURL(el.href);
    toast.success(`Exported ${picked.length} employee(s)`);
  };

  const delSelected = async () => {
    const picked = rows.filter((r) => selected.has(r.employee_id));
    if (picked.length === 0) return toast.error("Select at least one employee");
    if (!window.confirm(`Delete ${picked.length} employee(s)? Their salary history will be removed.`)) return;
    let ok = 0, fail = 0;
    for (const r of picked) { try { await api.delete(`/employees/${r.employee_id}`); ok++; } catch { fail++; } }
    toast[fail ? "info" : "success"](`${ok} deleted${fail ? `, ${fail} failed` : ""}`);
    load();
  };

  return (
    <Layout>
      <div className="p-8 max-w-6xl mx-auto">
        <PageHeader title="Salary Ledger" subtitle="Per-day = monthly salary ÷ 30. Enter present days; paid leaves (2 or 4) auto-add when present ≥ 24. Pay posts to Cash Book expenses.">
          <input data-testid="salary-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="h-10 px-3 rounded-md border border-input bg-card text-sm num" />
          {canWrite && (
            <>
              <input ref={attRef} data-testid="salary-attendance-file" type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => importAttendance(e.target.files?.[0])} />
              <button data-testid="salary-attendance-import-btn" disabled={importing} onClick={() => attRef.current?.click()}
                title="Upload the monthly attendance report exported from Attendo (Excel or CSV)"
                className="h-10 px-4 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors disabled:opacity-40">
                <Upload size={15} /> {importing ? "Importing…" : "Import Attendo"}
              </button>
              <EmpCodeImport onDone={load} />
            </>
          )}
          <button data-testid="salary-register-btn" onClick={() => setRegOpen(true)}
            className="h-10 px-4 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors">
            <Users size={15} /> Register
          </button>
          <SalaryRegister open={regOpen} onOpenChange={setRegOpen} month={month} />
          <button data-testid="salary-print-all-slips-btn" disabled={rows.length === 0 || printingAll} onClick={printAllSlips}
            className="h-10 px-4 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors disabled:opacity-40">
            <Printer size={15} /> {printingAll ? "Preparing…" : "Print all slips"}
          </button>
          {canWrite && (
            <button data-testid="salary-final-all-btn" disabled={finRows.length === 0} onClick={() => setFinAll(true)}
              className="h-10 px-4 rounded-md border border-primary/40 text-primary text-sm font-medium flex items-center gap-2 hover:bg-primary/10 transition-colors disabled:opacity-40">
              <Lock size={15} /> Finalise all
            </button>
          )}
          {canWrite && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild>
                <button data-testid="add-employee-btn" className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors">
                  <UserPlus size={16} /> Employee
                </button>
              </DialogTrigger>
              <DialogContent className="bg-card">
                <DialogHeader><DialogTitle className="font-display text-2xl">{edit ? "Edit Employee" : "New Employee"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={label}>Employee ID</label>
                      <input data-testid="emp-code" className={field} value={form.emp_code} onChange={(e) => setForm({ ...form, emp_code: e.target.value })} placeholder="e.g. EMP-001" /></div>
                    <div><label className={label}>Full name</label>
                      <input data-testid="emp-name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={label}>Monthly salary (₹)</label>
                      <input data-testid="emp-salary" type="number" className={field} value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })} /></div>
                    <div><label className={label}>Designation</label>
                      <input data-testid="emp-designation" className={field} value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="Driver, Clerk…" /></div>
                  </div>
                  <div><label className={label}>Phone</label>
                    <input data-testid="emp-phone" className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="optional" /></div>
                  <div><label className={label}>Leave type</label>
                    <select data-testid="emp-leave-type" className={field} value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}>
                      {LEAVE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">Per-day = salary ÷ 30. "With leaves" adds 2 or 4 paid days when present ≥ 24.</p>
                  </div>
                </div>
                <DialogFooter>
                  <button data-testid="emp-save" onClick={saveEmp} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">{edit ? "Save changes" : "Add employee"}</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </PageHeader>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">Employees</div><div className="num text-xl font-bold">{rows.length}</div></div>
          <div className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-3"><div className="text-xs text-muted-foreground">Net payable (month)</div><div className="num text-xl font-bold text-accent">{inr(totalNet)}</div></div>
          <div className="rounded-lg border border-[hsl(var(--positive))]/20 bg-[hsl(var(--positive))]/5 px-4 py-3"><div className="text-xs text-muted-foreground">Paid this month</div><div className="num text-xl font-bold text-[hsl(var(--positive))]">{inr(totalPaid)}</div></div>
        </div>

        <SalaryTrend month={month} refreshKey={rows} />

        <Dialog open={!!importRes} onOpenChange={(o) => { if (!o) setImportRes(null); }}>
          <DialogContent data-testid="attendance-import-result" className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl">Attendo import · {month}</DialogTitle></DialogHeader>
            {importRes && (
              <div className="space-y-3 text-sm max-h-[60vh] overflow-y-auto">
                <div data-testid="attendance-import-updated" className="text-[hsl(var(--positive))] font-medium">{importRes.updated.length} employee(s) updated — review, then Save / Final.</div>
                {importRes.skipped.length > 0 && (
                  <div>
                    <div className="font-medium text-destructive mb-1">{importRes.skipped.length} row(s) skipped</div>
                    {importRes.skipped.map((s, i) => (
                      <div key={`${s.row}-${s.ref}`} data-testid={`attendance-skip-${i}`} className="text-xs text-muted-foreground">Row {s.row} · {s.ref}: {s.reason}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {selected.size > 0 && (
          <div data-testid="salary-bulk-bar" className="flex flex-wrap items-center gap-2 mb-4 rounded-lg border border-accent/30 bg-accent/5 px-4 py-2.5">
            <span className="text-sm font-medium">{selected.size} selected</span>
            <div className="flex-1" />
            <button data-testid="salary-bulk-export" onClick={exportSelected}
              className="h-9 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors">
              <Download size={14} /> Export CSV
            </button>
            {canWrite && (
              <button data-testid="salary-bulk-del" onClick={delSelected}
                className="h-9 px-3 rounded-md border border-destructive/30 bg-card text-sm text-destructive flex items-center gap-1.5 hover:bg-destructive/10 transition-colors">
                <Trash2 size={14} /> Delete selected
              </button>
            )}
            <button data-testid="salary-bulk-clear" onClick={() => setSelected(new Set())}
              className="h-9 px-3 rounded-md border border-input bg-card text-sm hover:bg-muted transition-colors">Clear</button>
          </div>
        )}

        {rows.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
            <Users className="mx-auto mb-3 opacity-40" size={32} /> No employees yet. Add one to start the salary ledger.
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-x-auto animate-fade-up">
            <div className="min-w-[880px]">
              <div className="grid grid-cols-[auto_1.4fr_auto_auto_auto_auto_auto] gap-3 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <input data-testid="salary-select-all" type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                <span>Employee</span><span className="text-right">Salary</span><span className="text-center">Present days</span><span className="text-right">Net</span><span className="text-center">Status</span><span className="text-right">Action</span>
              </div>
              <div className="divide-y divide-border">
                {rows.map((r) => (
                  <div key={r.employee_id} data-testid={`salary-row-${r.employee_id}`} className={`grid grid-cols-[auto_1.4fr_auto_auto_auto_auto_auto] gap-3 px-5 py-3 items-center ${selected.has(r.employee_id) ? "bg-accent/5" : ""}`}>
                    <input data-testid={`salary-select-${r.employee_id}`} type="checkbox" checked={selected.has(r.employee_id)} onChange={() => toggleSel(r.employee_id)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                    <div className="min-w-0">
                      <button data-testid={`emp-open-ledger-${r.employee_id}`} onClick={() => window.open(`/salary/${r.employee_id}/ledger?c=${getTabCompany()}`, "_blank")}
                        className="font-medium truncate text-left hover:text-primary transition-colors block w-full" title="Open full ledger in new tab">
                        {r.emp_code ? <span className="num text-[10px] bg-muted px-1.5 py-0.5 rounded mr-1.5 text-muted-foreground align-middle">{r.emp_code}</span> : null}{r.name}
                      </button>
                      <div className="text-xs text-muted-foreground truncate flex items-center gap-1.5">
                        {r.designation || "—"} · {leaveLabel(r.leave_type)}
                        {assetCounts[r.employee_id] > 0 && (
                          <span data-testid={`emp-asset-flag-${r.employee_id}`} className="inline-flex items-center gap-1 text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded text-[10px] font-medium">
                            <Wrench size={10} /> {assetCounts[r.employee_id]} asset{assetCounts[r.employee_id] > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="num text-sm text-right">{inr(r.monthly_salary)}</span>
                    <div className="flex flex-col items-center">
                      <input data-testid={`salary-present-${r.employee_id}`} type="number" min="0" disabled={!canWrite || r.status === "paid" || r.status === "final"}
                        className="w-16 h-9 px-2 rounded-md border border-input bg-card text-sm num text-center"
                        value={present[r.employee_id] ?? r.present_days}
                        onChange={(e) => setPresent({ ...present, [r.employee_id]: e.target.value })} />
                      <span data-testid={`salary-paiddays-${r.employee_id}`} className="num text-[10px] text-muted-foreground mt-0.5">paid {paidDaysLive(r)}d</span>
                    </div>
                    <div className="text-right">
                      <div data-testid={`salary-net-${r.employee_id}`} className="num text-sm font-bold text-[hsl(var(--positive))]">{inr(netLive(r))}</div>
                      {r.advance_total > 0 && <div className="num text-[10px]"><span className="text-destructive">adv −{inr(r.advance_total)}</span> · <span className="text-foreground font-medium">pay {inr(Math.max(netLive(r) - r.advance_total, 0))}</span></div>}
                    </div>
                    <div className="flex justify-center"><span className={`text-xs px-2 py-1 rounded-full capitalize font-medium ${statusColors[r.status]}`}>{r.status}</span></div>
                    <div className="flex items-center gap-2 justify-end">
                      {canWrite && r.status !== "paid" && (
                        <>
                          {r.status !== "final" && (
                            <>
                              <button data-testid={`salary-save-${r.employee_id}`} onClick={() => saveLeave(r)} className="h-8 px-2.5 rounded-md border border-input text-xs hover:bg-muted transition-colors">Save</button>
                              <button data-testid={`salary-final-${r.employee_id}`} onClick={() => setFin(r)} title="Month end final" className="h-8 px-2.5 rounded-md border border-primary/40 text-primary text-xs flex items-center gap-1 hover:bg-primary/10 transition-colors"><Lock size={12} /> Final</button>
                            </>
                          )}
                          {r.status === "final" && isAdmin && (
                            <button data-testid={`salary-unfinal-${r.employee_id}`} onClick={() => undoFinal(r)} title="Undo month end final" className="h-8 px-2.5 rounded-md border border-input text-xs flex items-center gap-1 hover:bg-muted transition-colors"><Undo2 size={12} /> Undo</button>
                          )}
                          <button data-testid={`salary-adv-${r.employee_id}`} onClick={() => { setAdv(r); setAdvForm({ amount: "", mode: "cash", note: "", date: today() }); }} className="h-8 px-2.5 rounded-md border border-input text-xs hover:bg-muted transition-colors">Adv</button>
                          <button data-testid={`salary-pay-${r.employee_id}`} onClick={() => { setPay(r); setPayMode("cash"); setPayDate(today()); }} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs flex items-center gap-1 hover:bg-primary/90 transition-colors"><Wallet size={13} /> Pay</button>
                        </>
                      )}
                      {r.status === "paid" && <span className="text-xs text-muted-foreground num">{r.paid_date}</span>}
                      <button data-testid={`salary-slip-${r.employee_id}`} onClick={() => downloadSlip(r)} title="Print salary slip for this month" className="h-8 px-2.5 rounded-md border border-input text-xs flex items-center gap-1 hover:bg-muted transition-colors"><FileDown size={13} /> Slip</button>
                      <button data-testid={`salary-advlog-${r.employee_id}`} onClick={() => openAdvLog(r)} title="Advance history" className="h-8 px-2.5 rounded-md border border-input text-xs hover:bg-muted transition-colors">Log</button>
                      {canWrite && (
                        <>
                          <button data-testid={`emp-edit-${r.employee_id}`} onClick={() => openEdit(r)} className="text-muted-foreground hover:text-primary transition-colors"><Pencil size={14} /></button>
                          <button data-testid={`emp-del-${r.employee_id}`} onClick={() => delEmp(r)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={14} /></button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <Dialog open={!!pay} onOpenChange={(o) => !o && setPay(null)}>
          <DialogContent className="bg-card max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Pay Salary · {pay?.name}</DialogTitle></DialogHeader>
            {pay && (
              <div className="space-y-3">
                <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Month</span><span className="num">{pay.month}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Present / Paid days</span><span className="num">{present[pay.employee_id] ?? pay.present_days} / {paidDaysLive(pay)}</span></div>
                  {pay.advance_total > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Advance deducted</span><span className="num">- {inr(pay.advance_total)}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Net payable now</span><span className="num font-bold text-accent">{inr(pay.payable != null ? pay.payable : netLive(pay))}</span></div>
                </div>
                <div>
                  <label className={label}>Payment date</label>
                  <input data-testid="salary-pay-date" type="date" className={field} value={payDate}
                    onChange={(e) => setPayDate(e.target.value)} />
                </div>
                <div>
                  <label className={label}>Pay from</label>
                  <div className="grid grid-cols-2 gap-2">
                    {["cash", "bank"].map((m) => (
                      <button key={m} type="button" data-testid={`salary-paymode-${m}`} onClick={() => setPayMode(m)}
                        className={`h-10 rounded-md text-sm capitalize border transition-colors ${payMode === m ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{m}</button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SignaturePad ref={payCashierRef} label="Cashier signature" testid="salary-sign-cashier" />
                  <SignaturePad ref={payReceiverRef} label="Receiver signature" testid="salary-sign-receiver" />
                </div>
                <p className="text-xs text-muted-foreground">This generates a Salary voucher and posts the payment to the Cash Book on the selected date, and marks the salary as paid.</p>
              </div>
            )}
            <DialogFooter>
              <button data-testid="salary-confirm-pay" onClick={doPay} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Confirm payment</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={finAll} onOpenChange={setFinAll}>
          <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Finalise all · {month}</DialogTitle></DialogHeader>
            <div data-testid="salary-final-all-dialog" className="text-sm">
              <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-3 gap-y-1.5 items-center">
                <span className="text-xs text-muted-foreground">Employee</span><span className="text-xs text-muted-foreground text-right">Days</span><span className="text-xs text-muted-foreground text-right">Gross</span><span className="text-xs text-muted-foreground text-right">Adv</span><span className="text-xs text-muted-foreground text-right">Payable</span>
                {finRows.map((r) => (
                  <React.Fragment key={r.employee_id}>
                    <span className="truncate">{r.name}</span>
                    <span className="num text-right">{paidDaysLive(r)}</span>
                    <span className="num text-right text-[hsl(var(--positive))]">{inr(netLive(r))}</span>
                    <span className="num text-right text-destructive">{r.advance_total ? `−${inr(r.advance_total)}` : "—"}</span>
                    <span className="num text-right font-medium">{inr(finPay(r))}</span>
                  </React.Fragment>
                ))}
              </div>
              <div className="flex justify-between border-t border-border mt-3 pt-2 font-semibold">
                <span>{finRows.length} employee(s) · total payable</span>
                <span data-testid="salary-final-all-total" className="num">{inr(finRows.reduce((s, r) => s + finPay(r), 0))}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Uses the present days shown on screen. Locks attendance and posts each salary as a credit in the employee ledger. Already final or paid employees are skipped.</p>
            </div>
            <DialogFooter>
              <button data-testid="salary-final-all-confirm" onClick={doFinalAll} className="h-10 px-5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors">Confirm finalise all</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!fin} onOpenChange={(o) => !o && setFin(null)}>
          <DialogContent className="bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl">Month End Final · {fin?.name}</DialogTitle></DialogHeader>
            {fin && (
              <div data-testid="salary-final-dialog" className="rounded-md bg-muted p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Month</span><span className="num">{month}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Present / Paid days</span><span className="num">{present[fin.employee_id] ?? fin.present_days} / {paidDaysLive(fin)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Gross salary (credit)</span><span className="num text-[hsl(var(--positive))]">{inr(netLive(fin))}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Advances (debit)</span><span className="num text-destructive">− {inr(fin.advance_total)}</span></div>
                <div className="flex justify-between border-t border-border pt-1"><span className="font-medium">Final payable</span><span data-testid="salary-final-payable" className="num font-bold">{inr(Math.max(netLive(fin) - fin.advance_total, 0))}</span></div>
                <p className="text-xs text-muted-foreground pt-2">Locks attendance for {month} and posts the salary as a credit in the employee ledger. Pay later as usual.</p>
              </div>
            )}
            <DialogFooter>
              <button data-testid="salary-final-confirm" onClick={doFinal} className="h-10 px-5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors">Confirm final</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!adv} onOpenChange={(o) => !o && setAdv(null)}>
          <DialogContent className="bg-card max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Salary Advance · {adv?.name}</DialogTitle></DialogHeader>
            {adv && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={label}>Advance amount (₹)</label>
                    <input data-testid="adv-amount" type="number" className={field} value={advForm.amount}
                      onChange={(e) => setAdvForm({ ...advForm, amount: e.target.value })} />
                  </div>
                  <div>
                    <label className={label}>Pay from</label>
                    <div className="grid grid-cols-2 gap-2">
                      {["cash", "bank"].map((m) => (
                        <button key={m} type="button" data-testid={`adv-mode-${m}`} onClick={() => setAdvForm({ ...advForm, mode: m })}
                          className={`h-10 rounded-md text-xs capitalize border transition-colors ${advForm.mode === m ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{m}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className={label}>Payment date</label>
                  <input data-testid="adv-date" type="date" className={field} value={advForm.date}
                    onChange={(e) => setAdvForm({ ...advForm, date: e.target.value })} />
                </div>
                <div>
                  <label className={label}>Note</label>
                  <input data-testid="adv-note" className={field} value={advForm.note}
                    onChange={(e) => setAdvForm({ ...advForm, note: e.target.value })} placeholder="optional" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SignaturePad ref={advCashierRef} label="Cashier signature" testid="adv-sign-cashier" />
                  <SignaturePad ref={advReceiverRef} label="Receiver signature" testid="adv-sign-receiver" />
                </div>
                <p className="text-xs text-muted-foreground">Posts an expense (category "Salary Advance") in the Cash Book and deducts from this month's net salary ({adv.month}).</p>
              </div>
            )}
            <DialogFooter>
              <button data-testid="adv-save" onClick={saveAdvance} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Pay advance</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!advLog} onOpenChange={(o) => !o && setAdvLog(null)}>
          <DialogContent className="bg-card max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Advance History · {advLog?.name}</DialogTitle></DialogHeader>
            <div className="space-y-2">
              {advLogRows.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">No advances given yet.</div>
              ) : advLogRows.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium num">{inr(a.amount)} <span className="text-xs text-muted-foreground capitalize">· {a.mode}</span></div>
                    <div className="text-xs text-muted-foreground num">{a.date} · for {a.month}{a.note ? ` · ${a.note}` : ""}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${a.recovered ? "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]" : "bg-accent/15 text-accent"}`}>{a.recovered ? "Recovered" : "Outstanding"}</span>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
